import { UID } from '@fullstory/util/uid';
import { createAction } from '../types';
import { RequestDispatch } from '../../types';

export const createAppCacheActions =
  <
    L extends string,
    F extends string,
    R extends string,
    D extends string,
    Fr extends string,
    S extends string,
    C extends string,
  >(
    loading: L,
    failed: F,
    refetching: R,
    hasData: D,
    fresh: Fr,
    swap: S,
    clear: C,
  ) =>
  <T>() => ({
    appCacheIsLoading: (storageId: string, id: UID) =>
      createAction({
        type: loading,
        storageId,
        id,
      }),
    appCacheLoadFailed: (storageId: string, safeMessage: string | null, id: UID) =>
      createAction({
        type: failed,
        storageId,
        safeMessage,
        id,
      }),
    appCacheIsRefetching: (storageId: string, data: T, id: UID) =>
      createAction({
        type: refetching,
        storageId,
        data,
        id,
      }),
    appCacheHasData: (storageId: string, data: T, id: UID) =>
      createAction({
        type: hasData,
        storageId,
        data,
        id,
      }),
    appCacheFresh: (storageId: string, data: T, id: UID) =>
      createAction({
        type: fresh,
        storageId,
        data,
        id,
      }),
    appCacheSwap: (storageId: string) =>
      createAction({
        type: swap,
        storageId,
      }),
    appCacheClear: (storageId: string) =>
      createAction({
        type: clear,
        storageId,
      }),
  });

export interface BasePayload<T extends string> {
  type: T;
  storageId: string;
  id: UID;
}
export interface FailedPayload<F extends string> {
  type: F;
  storageId: string;
  safeMessage: string | null;
  id: UID;
}
export interface DataPayload<S extends string, T> {
  type: S;
  storageId: string;
  data: T;
  id: UID;
}

export type AppCacheActions<
  L extends string,
  F extends string,
  R extends string,
  D extends string,
  Fr extends string,
  S extends string,
  C extends string,
  T,
> =
  | BasePayload<L>
  | FailedPayload<F>
  | DataPayload<R, T>
  | DataPayload<D, T>
  | DataPayload<Fr, T>
  | BasePayload<S>
  | BasePayload<C>;

/**
 * Helper to create the redux async action required to meet the app cache agreement
 * between client and server. Cancellation should be handled in more specific redux
 * modules.
 *
 * This allows you to pass in the parallel AppCacheActions generated through
 * `createAppCacheActions`, a single query which returns a promise to the data
 * you need, and the two checks for app caching, if data is similar, or if it's
 * "fresh" (often checked from the difference from the computed at timestamp, and
 * the cache options).
 */
export const createAppCacheRequestAction =
  <Data, Payload>() =>
  <L extends string, F extends string, R extends string, D extends string, Fr extends string>({
    query,
    loading,
    failed,
    refetching,
    hasData,
    fresh,
    isSimiliar,
    isDataFresh,
    forceSwap,
  }: {
    query: (dispatch: RequestDispatch<any>, payload: Payload, isRefresh: boolean) => Promise<Data>;
    loading: (storageId: string, id: UID) => BasePayload<L>;
    failed: (storageId: string, safeMessage: string, id: UID) => FailedPayload<F>;
    refetching: (storageId: string, data: Data, id: UID) => DataPayload<R, Data>;
    hasData: (storageId: string, data: Data, id: UID) => DataPayload<D, Data>;
    fresh: (storageId: string, data: Data, id: UID) => DataPayload<Fr, Data>;
    isSimiliar: (newData: Data, oldData: Data) => boolean;
    isDataFresh: (payload: Payload, data: Data) => boolean;
    forceSwap?: boolean;
  }) =>
  async (
    dispatch: RequestDispatch<any>,
    storageId: string,
    payload: Payload,
    isRefresh: boolean = false,
  ) => {
    const id = UID();
    dispatch(loading(storageId, id));
    let data: Data;

    try {
      data = await query(dispatch, payload, isRefresh);
    } catch (e: any) {
      dispatch(failed(storageId, e?.safeMessage ?? null, id));
      return;
    }

    if (isDataFresh(payload, data)) {
      dispatch(fresh(storageId, data, id));
      return;
    }
    dispatch(refetching(storageId, data, id));
    let refetchData: Data;

    try {
      refetchData = await query(dispatch, payload, true);
    } catch (e) {
      dispatch(fresh(storageId, data, id));
      return;
    }

    if (isSimiliar(refetchData, data)) {
      dispatch(fresh(storageId, data, id));
    } else if (forceSwap) {
      dispatch(fresh(storageId, refetchData, id));
    } else {
      dispatch(hasData(storageId, refetchData, id));
    }
  };
